import React, { useEffect, useRef, useState } from 'react';
import StateChip from './StateChip';
import { SONG_NOTE_STATUS } from '../../../../services/songNotesModel';
import './NoteRow.css';

const { CAPTURED } = SONG_NOTE_STATUS;

/**
 * One Song Note row — shared by the note list, the carry-forward expansion and
 * the Deliver dialog's preview (design handoff §Focus).
 *
 * Captured rows carry the full private treatment (dashed border, eye-slash
 * chip) AND the full-width "Deliver →" button: the delivery moment must be
 * unmissable without being noisy — a quiet outline button on the row itself,
 * not a toast, not a modal ambush.
 */

/** "just now · you" / "Jun 24 · Annelise" — meta line, oldest branch verbatim
 *  from the mockups. */
const formatMeta = (note, teacherName) => {
	const at = note.statusChangedAt?.toDate ? note.statusChangedAt.toDate() : note.statusChangedAt;
	let when = '';
	if (at instanceof Date && !Number.isNaN(at.getTime())) {
		const ageMs = Date.now() - at.getTime();
		if (ageMs < 90 * 1000) when = 'just now';
		else if (ageMs < 60 * 60 * 1000) when = `${Math.round(ageMs / 60000)} min`;
		else when = at.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
	}
	const who = teacherName || 'you';
	return [when, who].filter(Boolean).join(' · ');
};

/**
 * Edit + delete (Anthony, 2026-09-25). Both controls are ALWAYS rendered and
 * quieted with colour, never hover-revealed (keratoconus; the PWA has no hover).
 * Delete is two taps on the row itself, never a modal: the first tap arms it,
 * the second deletes. A delivered note says the family may have read it,
 * because deleting it takes it off their Songs page too. Editing never changes
 * who can see a note (useSongNotes → updateNoteText).
 */
const NoteRow = ({
	note,
	teacherName = '',
	onDeliver = null, // (note) => void — opens the Deliver dialog
	onEdit = null, // (noteId, text) => Promise<boolean>
	onDelete = null, // (noteId) => Promise<boolean>
	deleteWarnsFamily = false, // true when the note is delivered
	sections = null, // song sections offered in edit mode (2026-09-25); null = no picker
	onSetSection = null, // (noteId, section|null) => Promise<boolean>
	actions = null, // extra row actions — slot, not baked in
}) => {
	const isCaptured = note.status === CAPTURED;
	const [editing, setEditing] = useState(false);
	const [draft, setDraft] = useState(note.text);
	const [armed, setArmed] = useState(false);
	const [sectionDraft, setSectionDraft] = useState(note.sectionId || '');
	const showSectionPicker = Boolean(sections && sections.length && onSetSection);
	const fieldRef = useRef(null);
	const canAct = !note.pending;

	useEffect(() => {
		if (editing && fieldRef.current) {
			const el = fieldRef.current;
			el.focus();
			el.setSelectionRange(el.value.length, el.value.length);
		}
	}, [editing]);

	const startEdit = () => {
		if (!canAct) return;
		setDraft(note.text);
		setSectionDraft(note.sectionId || '');
		setArmed(false);
		setEditing(true);
	};
	const cancelEdit = () => {
		setEditing(false);
		setDraft(note.text);
	};
	const saveEdit = async () => {
		const text = draft.trim();
		if (!text) return; // an empty note is a delete, and delete has its own control
		setEditing(false);
		if (showSectionPicker && sectionDraft !== (note.sectionId || '')) {
			await onSetSection(note.id, sections.find((sec) => sec.id === sectionDraft) || null);
		}
		if (text !== note.text && onEdit) await onEdit(note.id, text);
	};
	const onFieldKey = (e) => {
		if (e.key === 'Escape') {
			e.preventDefault();
			e.stopPropagation(); // the lesson sheet/drawer also closes on Esc
			cancelEdit();
		} else if (e.key === 'Enter' && !e.shiftKey) {
			e.preventDefault();
			saveEdit();
		}
	};
	const confirmDelete = async () => {
		if (!canAct || !onDelete) return;
		setArmed(false);
		await onDelete(note.id);
	};

	const hasControls = Boolean(onEdit || onDelete);

	return (
		<div className={`ld-note ld-note--${note.status}${note.pending ? ' is-pending' : ''}`}>
			<div className="ld-note__top">
				<StateChip status={note.status} />
				<span className="ld-note__meta">{formatMeta(note, teacherName)}</span>
				{hasControls && !editing && !armed && (
					<span className="ld-note__tools">
						{onEdit && (
							<button
								type="button"
								className="ld-note__tool"
								aria-label="Edit note"
								title="Edit note"
								onClick={startEdit}
								disabled={!canAct}>
								<i className="fa-sharp fa-solid fa-pen" aria-hidden="true" />
							</button>
						)}
						{onDelete && (
							<button
								type="button"
								className="ld-note__tool"
								aria-label="Delete note"
								title="Delete note"
								onClick={() => canAct && setArmed(true)}
								disabled={!canAct}>
								<i className="fa-sharp fa-solid fa-trash" aria-hidden="true" />
							</button>
						)}
					</span>
				)}
			</div>

			{editing ? (
				<div className="ld-note__edit">
					<textarea
						ref={fieldRef}
						className="ld-note__field"
						aria-label="Edit note text"
						value={draft}
						rows={Math.min(6, Math.max(2, Math.ceil(draft.length / 48)))}
						onChange={(e) => setDraft(e.target.value)}
						onKeyDown={onFieldKey}
					/>
					{showSectionPicker && (
						<label className="ld-note__section">
							<span className="ld-note__sectionlabel">Section</span>
							<select
								className="ld-note__sectionselect"
								value={sectionDraft}
								onChange={(e) => setSectionDraft(e.target.value)}>
								<option value="">Whole song</option>
								{sections.map((sec) => (
									<option key={sec.id} value={sec.id}>
										{sec.label}
									</option>
								))}
								{/* A note filed under a section that no longer exists keeps its place until moved. */}
								{note.sectionId && !sections.some((sec) => sec.id === note.sectionId) && (
									<option value={note.sectionId}>{note.sectionLabel || 'Earlier section'}</option>
								)}
							</select>
						</label>
					)}
					<div className="ld-note__editbar">
						<button type="button" className="ld-note__btn" onClick={cancelEdit}>
							Cancel
						</button>
						<button
							type="button"
							className="ld-note__btn ld-note__btn--primary"
							onClick={saveEdit}
							disabled={!draft.trim()}>
							Save
						</button>
					</div>
				</div>
			) : (
				<div className="ld-note__body">{note.text}</div>
			)}

			{armed && (
				<div className="ld-note__confirm" role="group" aria-label="Confirm delete">
					<span className="ld-note__confirmtext">
						{deleteWarnsFamily ? 'Delete? The family may have already read this note.' : 'Delete this note?'}
					</span>
					<div className="ld-note__editbar">
						<button type="button" className="ld-note__btn" onClick={() => setArmed(false)}>
							Keep
						</button>
						<button type="button" className="ld-note__btn ld-note__btn--danger" onClick={confirmDelete}>
							<i className="fa-sharp fa-solid fa-trash" aria-hidden="true" />
							Delete
						</button>
					</div>
				</div>
			)}

			{isCaptured && onDeliver && !editing && !armed && (
				<button
					type="button"
					className="ld-note__deliver"
					onClick={() => onDeliver(note)}
					disabled={Boolean(note.pending)}>
					Deliver
					<i className="fa-sharp fa-solid fa-arrow-right" aria-hidden="true" />
				</button>
			)}
			{actions}
		</div>
	);
};

export default NoteRow;
